import * as React from "react";

import { cn } from "@/lib/utils";
import { AnimatedCounter } from "@/components/ui/animated-counter";
import { Sparkline } from "@/components/ui/sparkline";

/**
 * Phase 10B 2050 MetricCard
 * - Frosted card-2050 surface with magnetic 3D tilt on hover
 * - Animated gold thread accent (top-left, 56px, draws on mount)
 * - Animated counter (eases value up from 0)
 * - Optional sparkline (right-aligned)
 * - Optional delta pill (auto sign detection: + green / - rose)
 */

const deltaToneClasses = {
  positive: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
  negative: "bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300",
  neutral: "bg-ink-100 text-ink-700 dark:bg-ink-900/70 dark:text-ink-100",
  gold: "pill-2050 pill-gold",
};

function resolveDelta(delta) {
  if (delta == null) return null;
  if (typeof delta === "object") return { value: delta.value, tone: delta.tone || "neutral" };
  const str = String(delta).trim();
  let tone = "neutral";
  if (str.startsWith("+")) tone = "positive";
  else if (str.startsWith("-")) tone = "negative";
  return { value: str, tone };
}

const MetricCard = React.forwardRef(
  (
    {
      className,
      label,
      value,
      unit,
      delta,
      helperText,
      loading = false,
      empty = false,
      sparkline,            // array of numbers
      sparklineColor = "#C9A568",
      animate = true,
      ...props
    },
    ref
  ) => {
    const cardRef = React.useRef(null);

    // Magnetic 3D tilt on mouse-move
    const onMouseMove = React.useCallback((e) => {
      const el = cardRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const x = e.clientX - r.left;
      const y = e.clientY - r.top;
      const px = x / r.width;
      const py = y / r.height;
      const rx = (0.5 - py) * 4;
      const ry = (px - 0.5) * 6;
      el.style.transform =
        `perspective(800px) rotateX(${rx}deg) rotateY(${ry}deg) translateY(-1px)`;
      el.style.setProperty("--sx", px * 100 + "%");
      el.style.setProperty("--sy", py * 100 + "%");
    }, []);
    const onMouseLeave = React.useCallback(() => {
      if (cardRef.current) cardRef.current.style.transform = "";
    }, []);

    const resolvedDelta = resolveDelta(delta);
    const numericValue = typeof value === "number"
      ? value
      : typeof value === "string"
        ? parseFloat(value.replace(/[^0-9.-]/g, ""))
        : null;

    return (
      <div
        ref={(node) => {
          cardRef.current = node;
          if (typeof ref === "function") ref(node);
          else if (ref) ref.current = node;
        }}
        className={cn(
          "card-2050 tilt gold-thread p-5 relative overflow-hidden focus-2050",
          className
        )}
        onMouseMove={onMouseMove}
        onMouseLeave={onMouseLeave}
        {...props}
      >
        <span className="tilt-shine" aria-hidden="true" />
        <div className="flex items-start justify-between">
          <div className="min-w-0">
            {label && (
              <div className="text-[10px] uppercase tracking-[0.16em] text-ink-500 font-medium pt-3">
                {label}
              </div>
            )}
            <div className="mt-2 flex items-baseline gap-1.5">
              {loading ? (
                <div className="h-9 w-24 skeleton" />
              ) : empty ? (
                <div className="font-display text-4xl font-semibold tracking-tight nums text-ink-300 dark:text-ink-700">
                  —
                </div>
              ) : (
                <>
                  <div className="font-display text-4xl font-semibold tracking-tight nums">
                    {animate && numericValue != null && Number.isFinite(numericValue) ? (
                      <AnimatedCounter value={numericValue} />
                    ) : (
                      value
                    )}
                  </div>
                  {unit ? (
                    <span className="text-base text-ink-500 ml-0.5">{unit}</span>
                  ) : null}
                </>
              )}
            </div>
            <div className="mt-1.5 flex items-center gap-2 text-[11px] min-h-[16px]">
              {!loading && !empty && resolvedDelta ? (
                <span
                  className={cn(
                    "px-2 py-0.5 rounded-full font-medium",
                    deltaToneClasses[resolvedDelta.tone] || deltaToneClasses.neutral
                  )}
                >
                  {resolvedDelta.value}
                </span>
              ) : null}
              {helperText && !loading ? (
                <span className="text-ink-500 truncate">{helperText}</span>
              ) : null}
            </div>
          </div>
          {sparkline && sparkline.length > 1 ? (
            <Sparkline points={sparkline} stroke={sparklineColor} className="shrink-0 ml-3" />
          ) : null}
        </div>
      </div>
    );
  }
);
MetricCard.displayName = "MetricCard";

export { MetricCard };
